import { NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { IImage, IModelViewInterior } from '@ncg/data';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { PannellumGlobal, PannellumViewer } from 'pannellum/src/js/pannellum';
import { FeatureDetectionService } from '../../core/feature-detection.service';
import { SpinnerModule } from '../../spinner/spinner.module';
import { ModelViewerLeadComponent } from './model-viewer-lead.component';

declare let pannellum: PannellumGlobal;

@Component({
    selector: 'ncg-model-viewer-interior',
    template: `
        <div class="interior-view columns is-flex-direction-column">
            <div class="column">
                <div class="is-ratio-hd">
                    <div #panoramaContainer class="interior-view__container">
                        <ncg-spinner [active]="isLoading" [isCentered]="true"></ncg-spinner>
                        <div class="interior-view__controls" *ngIf="pannellumViewer">
                            <div class="interior-view__control" (click)="zoomInControl()">
                                <svg-icon-sprite
                                    class="is-flex"
                                    src="plus"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"></svg-icon-sprite>
                            </div>
                            <div class="interior-view__control" (click)="zoomOutControl()">
                                <svg-icon-sprite
                                    class="is-flex"
                                    src="minus"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"></svg-icon-sprite>
                            </div>
                            <div class="interior-view__control" (click)="fullscreenControl()">
                                <svg-icon-sprite
                                    class="is-flex"
                                    src="fullscreen"
                                    [viewBox]="'0 0 30 30'"
                                    [width]="'30px'"
                                    [height]="'30px'"></svg-icon-sprite>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="column pt-0" *ngIf="leadImages?.length">
                <ncg-model-viewer-lead (leadClick)="leadImageClicked($event)" [arr]="leadImages" [title]="selectedTitle"></ncg-model-viewer-lead>
            </div>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerModule, IconSpriteModule, ModelViewerLeadComponent, NgIf],
})
export class ModelViewerInteriorComponent implements AfterViewInit, OnInit {
    private selectedInteriorIndex = 0;

    public selectedTitle = '';
    public pannellumViewer: PannellumViewer;
    public isLoading = true;
    public leadImages: IImage[];

    @ViewChild('panoramaContainer', { static: true })
    public panoramaContainer: ElementRef<HTMLDivElement>;

    @Input()
    public interiorView: IModelViewInterior[];

    constructor(
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly cd: ChangeDetectorRef
    ) {}

    public ngOnInit() {
        this.selectedTitle = this.interiorView[0]?.title ?? '';
        this._createImageLeads();
    }

    public ngAfterViewInit() {
        if (this.featureDetectionService.isBrowser()) {
            this._createView(this.interiorView);
        }
    }

    public zoomInControl() {
        this.pannellumViewer.setHfov(this.pannellumViewer.getHfov() - 10);
    }

    public zoomOutControl() {
        this.pannellumViewer.setHfov(Number(this.pannellumViewer.getHfov()) + 10);
    }

    public fullscreenControl() {
        this.pannellumViewer.toggleFullscreen();
    }

    public leadImageClicked(index: number) {
        if (this.selectedInteriorIndex === index) {
            return;
        }
        this.isLoading = true;
        this.selectedInteriorIndex = index;
        this.selectedTitle = this.interiorView[index].title;
        this.pannellumViewer.loadScene(
            String(this.selectedInteriorIndex),
            this.pannellumViewer.getPitch(),
            this.pannellumViewer.getYaw(),
            this.pannellumViewer.getHfov()
        );
    }

    private _createImageLeads() {
        const images: IImage[] = [];
        this.interiorView.forEach((view) => {
            if (view.leadInImage) {
                images.push(view.leadInImage);
            }
        });

        this.leadImages = images;
        this.cd.detectChanges();
    }

    private async _createView(interiors: IModelViewInterior[]) {
        const el = this.panoramaContainer.nativeElement;
        if (!el) {
            return;
        }

        try {
            await import('pannellum/src/js/pannellum');
            await import('pannellum/src/js/libpannellum');

            this.pannellumViewer = pannellum.viewer(el, {
                default: {
                    firstScene: '0',
                    sceneFadeDuration: 1000,
                    autoLoad: false,
                    mouseZoom: false,
                    showControls: false,
                },
                scenes: {},
            });

            interiors.forEach((interior, index) => {
                this.pannellumViewer.addScene(String(index), {
                    type: 'equirectangular',
                    panorama: interior.image?.url,
                });
            });

            this.pannellumViewer.loadScene(String(this.selectedInteriorIndex), 0, 0, 120);

            this.pannellumViewer.on('load', () => {
                this.isLoading = false;
                this.cd.markForCheck();
            });

            this.cd.markForCheck();
        } catch (err) {
            console.error('Failed to load Pannellum:', err);
            this.isLoading = false;
            this.cd.markForCheck();
        }
    }
}
